import { OrganizationType } from "../types/organization.type";
import { StatisticType } from "../types/dashboard.type";
import {
  getOperationalStatsForOrgType,
  newlyAddedOperationalStats,
} from "../constants/dashboardStats.catalog";

export function sanitizePickableIds<T extends string>(
  stored: unknown,
  all: readonly T[],
  newlyAdded: readonly T[] = [],
): T[] {
  const allowed = new Set(all);
  if (!Array.isArray(stored)) return [...all];
  const selected = stored.filter(
    (id): id is T => typeof id === "string" && allowed.has(id as T),
  );
  if (stored.length === 0) return [];
  if (selected.length === 0) return [...all];
  const selectedSet = new Set(selected);
  const extra = newlyAdded.filter((id) => allowed.has(id) && !selectedSet.has(id));
  return extra.length > 0 ? [...selected, ...extra] : selected;
}

/** Combined Customize item `scope_emissions` → separate Scope 1 / Scope 2 picks. */
export function expandLegacyScopeEmissions(stored: unknown): unknown {
  if (!Array.isArray(stored)) return stored;
  const next: unknown[] = [];
  const seen = new Set<string>();
  for (const id of stored) {
    const pieces = id === "scope_emissions" ? ["scope_1", "scope_2"] : [id];
    for (const piece of pieces) {
      if (typeof piece === "string") {
        if (seen.has(piece)) continue;
        seen.add(piece);
      }
      next.push(piece);
    }
  }
  return next;
}

export const ESG_OPERATIONAL_STATS_KEY = "selectedStats";

export function operationalStatsKeyForOrg(orgType: OrganizationType): string {
  return `esgSelectedStats:${orgType}`;
}

/** Load Monthly operational picks for this org type; migrate the shared key once. */
export function loadOperationalStatsForOrg(
  orgType: OrganizationType,
): StatisticType[] {
  const allowed = getOperationalStatsForOrgType(orgType);
  const perOrgRaw = sessionStorage.getItem(operationalStatsKeyForOrg(orgType));
  if (perOrgRaw) {
    try {
      return sanitizePickableIds(JSON.parse(perOrgRaw), allowed);
    } catch {
      return [...allowed];
    }
  }
  const legacyRaw = sessionStorage.getItem(ESG_OPERATIONAL_STATS_KEY);
  if (legacyRaw) {
    try {
      return sanitizePickableIds(
        JSON.parse(legacyRaw),
        allowed,
        newlyAddedOperationalStats(orgType),
      );
    } catch {
      return [...allowed];
    }
  }
  return [...allowed];
}

export function persistOperationalStatsForOrg(
  orgType: OrganizationType,
  stats: StatisticType[],
): void {
  const payload = JSON.stringify(stats);
  sessionStorage.setItem(operationalStatsKeyForOrg(orgType), payload);
  sessionStorage.setItem(ESG_OPERATIONAL_STATS_KEY, payload);
}
